import {
  runBulk,
  type BulkAction,
} from "@/components/molecules/tables/bulk-actions";
import { useHasRole } from "@/features/clients/hooks/use-has-role";
import { useDeleteExternalAccount } from "@/features/external-accounts/hooks/use-external-accounts";
import type { ExternalAccount } from "@/features/external-accounts/types";
import { useTranslation } from "@/features/utils/i18n";
import { TrashIcon } from "@phosphor-icons/react";
import { useMemo } from "react";

export const useExternalAccountsBulkActions =
  (): BulkAction<ExternalAccount>[] => {
    const { t } = useTranslation("externalAccounts");
    const isOwner = useHasRole("OWNER");
    const deleteAccount = useDeleteExternalAccount();

    return useMemo(() => {
      if (!isOwner) return [];

      return [
        {
          key: "delete",
          label: t("externalAccounts.actions.deleteSelected"),
          icon: <TrashIcon className="h-4 w-4" />,
          variant: "destructive",
          confirm: (rows) =>
            t("externalAccounts.messages.bulkDeleteConfirm", {
              count: rows.length,
            }),
          onAction: async (rows) => {
            await runBulk(
              rows,
              (account) => deleteAccount.mutateAsync(account.id),
              {
                success: (count) =>
                  t("externalAccounts.messages.bulkDeleteSuccess", { count }),
                error: (count) =>
                  t("externalAccounts.messages.bulkDeleteError", { count }),
              },
            );
          },
        },
      ];
    }, [isOwner, t, deleteAccount]);
  };
